import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell,
  Camera,
  CheckCircle2,
  Globe2,
  KeyRound,
  Languages,
  LogOut,
  Mail,
  Save,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import AdirePattern from '../../components/shared/AdirePattern';
import { getInitials, getPersonName, getProfilePhotoUrl } from '../../lib/profileUtils';
import { getApiErrorMessage } from '../../lib/apiError';

const SECTIONS = [
  { key: 'profile', label: 'Profile', icon: UserRound },
  { key: 'security', label: 'Security', icon: KeyRound },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'preferences', label: 'Role preferences', icon: ShieldCheck },
];

const NOTIFICATION_OPTIONS = [
  ['visitUpdates', 'Visit updates', 'Reports, visit completion, and care status changes.'],
  ['alerts', 'Alerts', 'Urgent and routine care alerts.'],
  ['careExpenses', 'Care expenses', 'Subscriber approval requests and expense status.'],
  ['messages', 'Messages', 'New in-app messages and conversation updates.'],
  ['scheduleChanges', 'Schedule changes', 'Visit schedule approvals, changes, and reminders.'],
  ['reviewUpdates', 'Review updates', 'Supervisor approvals, flags, and review responses.'],
  ['adminSecurity', 'Admin and security', 'Account security and administrative notices.'],
];

const ROLE_PREFERENCE_CONFIG = {
  subscriber: [
    { key: 'alertDigest', label: 'Alert digest', type: 'select', options: ['Immediate', 'Daily', 'Weekly'] },
    { key: 'expenseApprovalReminders', label: 'Care expense approval reminders', type: 'checkbox' },
  ],
  dependant: [
    { key: 'medicationReminders', label: 'Medication reminders', type: 'checkbox' },
    { key: 'visitReminders', label: 'Visit reminders', type: 'checkbox' },
  ],
  care_officer: [
    { key: 'availabilityReminders', label: 'Availability reminders', type: 'checkbox' },
    { key: 'visitReminderLeadMinutes', label: 'Visit reminder lead time', type: 'number', suffix: 'minutes' },
  ],
  supervisor: [
    { key: 'reviewQueueAlerts', label: 'Review queue alerts', type: 'checkbox' },
    { key: 'visitStartedAlerts', label: 'Visit started alerts', type: 'checkbox' },
    { key: 'scheduleGenerationAlerts', label: 'Scheduling alerts', type: 'checkbox' },
  ],
  customer_care: [
    { key: 'messageQueueAlerts', label: 'Message queue alerts', type: 'checkbox' },
    { key: 'emergencyEscalationAlerts', label: 'Emergency escalation alerts', type: 'checkbox' },
  ],
  admin: [
    { key: 'securityAlerts', label: 'Security alerts', type: 'checkbox' },
    { key: 'weeklyMetricsEmail', label: 'Weekly metrics email', type: 'checkbox' },
  ],
};

const roleLabels = {
  subscriber: 'Subscriber',
  dependant: 'Dependant',
  care_officer: 'Care officer',
  supervisor: 'Supervisor',
  customer_care: 'Customer care',
  admin: 'Admin',
};

const passwordRequirements = [
  ['At least 12 characters', value => value.length >= 12],
  ['One uppercase letter', value => /[A-Z]/.test(value)],
  ['One lowercase letter', value => /[a-z]/.test(value)],
  ['One number', value => /\d/.test(value)],
  ['One special character', value => /[!@#$%^&*(),.?":{}|<>]/.test(value)],
];

const defaultNotifications = () => Object.fromEntries(
  NOTIFICATION_OPTIONS.map(([key]) => [key, { email: true, inApp: true }])
);

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`inline-flex min-w-[86px] items-center justify-between rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
      checked
        ? 'border-[#9F1D35] bg-[#9F1D35] text-[#FAFAF7]'
        : 'border-[#D8D5D0] bg-[#FAFAF7] text-[#5A5A5A]'
    }`}
    aria-pressed={checked}
  >
    {label}
    <span className={`ml-2 h-2 w-2 rounded-full ${checked ? 'bg-[#C9A961]' : 'bg-[#D8D5D0]'}`} />
  </button>
);

const SettingsPage = () => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const { authApi, refreshUser, logout, user } = useAuth();
  const { currentUser, role, fetchData } = useApp();
  const [activeSection, setActiveSection] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [settingsResponse, setSettingsResponse] = useState(null);
  const [profileForm, setProfileForm] = useState({ phone: '', timezone: 'Africa/Lagos', preferredLanguage: 'English' });
  const [notificationForm, setNotificationForm] = useState(defaultNotifications);
  const [rolePreferenceForm, setRolePreferenceForm] = useState({});
  const [phonePassword, setPhonePassword] = useState('');
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const accountUser = settingsResponse?.user || user || {};
  const accountProfile = settingsResponse?.profile || currentUser || {};
  const displayName = getPersonName(accountProfile, getPersonName(currentUser, 'Magnolia user'));
  const initials = getInitials(accountProfile, getInitials(currentUser));
  const photoUrl = getProfilePhotoUrl(accountUser) || getProfilePhotoUrl(accountProfile) || getProfilePhotoUrl(currentUser);
  const initialPhone = settingsResponse?.settings?.phone || '';
  const requiresPhonePassword = role === 'dependant' && profileForm.phone !== initialPhone;

  const rolePreferenceConfig = ROLE_PREFERENCE_CONFIG[role] || [];
  const passwordValid = passwordRequirements.every(([, test]) => test(passwordForm.newPassword));
  const passwordsMatch = passwordForm.newPassword && passwordForm.newPassword === passwordForm.confirmPassword;

  const applySettings = useCallback((data) => {
    const settings = data?.settings || {};
    setSettingsResponse(data);
    setProfileForm({
      phone: settings.phone || '',
      timezone: settings.timezone || 'Africa/Lagos',
      preferredLanguage: settings.preferredLanguage || 'English',
    });
    setNotificationForm({
      ...defaultNotifications(),
      ...(settings.notifications || {}),
    });
    setRolePreferenceForm(settings.rolePreferences || {});
    setPhonePassword('');
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authApi.get('/account/settings');
      applySettings(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to load settings.'));
    } finally {
      setLoading(false);
    }
  }, [applySettings, authApi]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const refreshAppState = async (data) => {
    applySettings(data);
    await refreshUser();
    await fetchData({ force: true });
  };

  const saveProfileAndPreferences = async () => {
    setSaving(true);
    try {
      const payload = {
        phone: profileForm.phone,
        timezone: profileForm.timezone,
        preferredLanguage: profileForm.preferredLanguage,
        notifications: notificationForm,
        rolePreferences: rolePreferenceForm,
      };
      if (requiresPhonePassword) {
        payload.currentPassword = phonePassword;
      }
      const response = await authApi.patch('/account/settings', payload);
      await refreshAppState(response.data);
      toast.success('Settings saved.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to save settings.'));
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authApi.post('/account/profile-photo', formData);
      await refreshAppState(response.data);
      toast.success('Profile photo updated.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to upload profile photo.'));
    } finally {
      setUploadingPhoto(false);
      event.target.value = '';
    }
  };

  const removePhoto = async () => {
    setUploadingPhoto(true);
    try {
      const response = await authApi.delete('/account/profile-photo');
      await refreshAppState(response.data);
      toast.success('Profile photo removed.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to remove profile photo.'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const changePassword = async () => {
    if (!passwordValid || !passwordsMatch) {
      toast.error('Check the new password requirements and confirmation.');
      return;
    }
    setSaving(true);
    try {
      await authApi.patch('/account/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password changed. Please sign in again.');
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to change password.'));
    } finally {
      setSaving(false);
    }
  };

  const updateNotification = (key, channel, value) => {
    setNotificationForm(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || { email: true, inApp: true }),
        [channel]: value,
      },
    }));
  };

  const updateRolePreference = (key, value) => {
    setRolePreferenceForm(prev => ({ ...prev, [key]: value }));
  };

  const renderSection = (() => {
    if (activeSection === 'security') {
      return (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Security</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Account status</p>
                <p className="mt-2 text-sm font-semibold capitalize text-[#1A1A1A]">{accountUser.status || 'Active'}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">KYC status</p>
                <p className="mt-2 text-sm font-semibold capitalize text-[#1A1A1A]">{accountUser.kycStatus || accountUser.kyc_status || 'Approved'}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Last login</p>
                <p className="mt-2 text-sm font-semibold text-[#1A1A1A]">{formatDateTime(accountUser.lastLoginAt || accountUser.last_login_at)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Change password</h3>
            <div className="mt-5 grid gap-4">
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(event) => setPasswordForm(prev => ({ ...prev, currentPassword: event.target.value }))}
                className="rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="Current password"
              />
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(event) => setPasswordForm(prev => ({ ...prev, newPassword: event.target.value }))}
                className="rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="New password"
              />
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(event) => setPasswordForm(prev => ({ ...prev, confirmPassword: event.target.value }))}
                className="rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="Confirm new password"
              />
              <div className="grid gap-2 sm:grid-cols-2">
                {passwordRequirements.map(([label, test]) => (
                  <p key={label} className={`flex items-center gap-2 text-xs ${test(passwordForm.newPassword) ? 'text-emerald-700' : 'text-[#5A5A5A]'}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {label}
                  </p>
                ))}
              </div>
              <button
                type="button"
                onClick={changePassword}
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60 sm:w-fit"
              >
                <KeyRound className="h-4 w-4" />
                Change password
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="inline-flex items-center gap-2 rounded-full border border-[#A33232] px-5 py-3 text-sm font-medium text-[#A33232] hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      );
    }

    if (activeSection === 'notifications') {
      return (
        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Notifications</h2>
              <p className="mt-1 text-sm text-[#5A5A5A]">Choose what reaches your email and what stays inside Magnolia.</p>
            </div>
            <button
              type="button"
              onClick={saveProfileAndPreferences}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
          <div className="mt-6 divide-y divide-[#E8E6E3]">
            {NOTIFICATION_OPTIONS.map(([key, label, description]) => (
              <div key={key} className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium text-[#1A1A1A]">{label}</p>
                  <p className="mt-1 text-sm text-[#5A5A5A]">{description}</p>
                </div>
                <div className="flex gap-2">
                  <Toggle checked={Boolean(notificationForm[key]?.email)} onChange={(value) => updateNotification(key, 'email', value)} label="Email" />
                  <Toggle checked={Boolean(notificationForm[key]?.inApp)} onChange={(value) => updateNotification(key, 'inApp', value)} label="In-app" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (activeSection === 'preferences') {
      return (
        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1A1A1A]">Role preferences</h2>
              <p className="mt-1 text-sm text-[#5A5A5A]">Safe preferences for your {roleLabels[role] || 'Magnolia'} workspace.</p>
            </div>
            <button
              type="button"
              onClick={saveProfileAndPreferences}
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
          </div>
          <div className="mt-6 grid gap-4">
            {rolePreferenceConfig.map((pref) => (
              <label key={pref.key} className="rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] p-4">
                <span className="text-sm font-medium text-[#1A1A1A]">{pref.label}</span>
                {pref.type === 'checkbox' && (
                  <span className="mt-3 flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={Boolean(rolePreferenceForm[pref.key])}
                      onChange={(event) => updateRolePreference(pref.key, event.target.checked)}
                      className="h-4 w-4 accent-[#9F1D35]"
                    />
                    <span className="text-sm text-[#5A5A5A]">Enabled</span>
                  </span>
                )}
                {pref.type === 'select' && (
                  <select
                    value={rolePreferenceForm[pref.key] || pref.options[0]}
                    onChange={(event) => updateRolePreference(pref.key, event.target.value)}
                    className="mt-3 w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                  >
                    {pref.options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                )}
                {pref.type === 'number' && (
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      value={rolePreferenceForm[pref.key] ?? 60}
                      onChange={(event) => updateRolePreference(pref.key, Number(event.target.value || 0))}
                      className="w-full rounded-2xl border border-[#D8D5D0] bg-[#FAFAF7] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                    />
                    {pref.suffix && <span className="text-sm text-[#5A5A5A]">{pref.suffix}</span>}
                  </div>
                )}
              </label>
            ))}
            {rolePreferenceConfig.length === 0 && (
              <p className="rounded-2xl border border-dashed border-[#D8D5D0] p-5 text-sm text-[#5A5A5A]">No role-specific preferences are available yet.</p>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.035} />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#9F1D35] text-xl font-semibold text-[#FAFAF7]">
                {photoUrl ? <img src={photoUrl} alt="" className="h-full w-full object-cover" /> : initials}
              </div>
              <div>
                <p className="caption text-[#9F1D35]">{roleLabels[role] || 'Magnolia account'}</p>
                <h1 className="mt-2 font-serif text-3xl font-semibold text-[#1A1A1A]">{displayName}</h1>
                <p className="mt-1 text-sm text-[#5A5A5A]">{accountUser.email || accountUser.phone || 'No login contact listed'}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPhoto} className="hidden" />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="inline-flex items-center gap-2 rounded-full bg-[#9F1D35] px-4 py-2.5 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
              >
                <Camera className="h-4 w-4" />
                {uploadingPhoto ? 'Uploading...' : 'Upload photo'}
              </button>
              {photoUrl && (
                <button
                  type="button"
                  onClick={removePhoto}
                  disabled={uploadingPhoto}
                  className="inline-flex items-center gap-2 rounded-full border border-[#A33232] px-4 py-2.5 text-sm font-medium text-[#A33232] disabled:opacity-60"
                >
                  <Trash2 className="h-4 w-4" />
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className="text-sm font-medium text-[#1A1A1A]">Full name</span>
              <input value={displayName} readOnly className="mt-2 w-full rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] px-4 py-3 text-sm text-[#5A5A5A]" />
            </label>
            <label>
              <span className="text-sm font-medium text-[#1A1A1A]">Email</span>
              <input value={accountUser.email || ''} readOnly className="mt-2 w-full rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] px-4 py-3 text-sm text-[#5A5A5A]" />
            </label>
            <label>
              <span className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]"><Smartphone className="h-4 w-4 text-[#9F1D35]" />Phone</span>
              <input
                value={profileForm.phone}
                onChange={(event) => setProfileForm(prev => ({ ...prev, phone: event.target.value }))}
                className="mt-2 w-full rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                placeholder="+234..."
              />
            </label>
            <label>
              <span className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]"><Globe2 className="h-4 w-4 text-[#9F1D35]" />Timezone</span>
              <select
                value={profileForm.timezone}
                onChange={(event) => setProfileForm(prev => ({ ...prev, timezone: event.target.value }))}
                className="mt-2 w-full rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
              >
                <option value="Africa/Lagos">Africa/Lagos (WAT)</option>
                <option value="Europe/London">Europe/London</option>
                <option value="America/New_York">America/New_York</option>
                <option value="America/Chicago">America/Chicago</option>
                <option value="America/Toronto">America/Toronto</option>
                <option value="America/Los_Angeles">America/Los_Angeles</option>
                <option value="Australia/Sydney">Australia/Sydney</option>
              </select>
            </label>
            <label>
              <span className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]"><Languages className="h-4 w-4 text-[#9F1D35]" />Preferred language</span>
              <select
                value={profileForm.preferredLanguage}
                onChange={(event) => setProfileForm(prev => ({ ...prev, preferredLanguage: event.target.value }))}
                className="mt-2 w-full rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
              >
                {['English', 'Yoruba', 'Igbo', 'Hausa', 'Twi', 'French'].map((language) => (
                  <option key={language} value={language}>{language}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-sm font-medium text-[#1A1A1A]">Role</span>
              <input value={roleLabels[role] || role || ''} readOnly className="mt-2 w-full rounded-2xl border border-[#E8E6E3] bg-[#F7F4EE] px-4 py-3 text-sm text-[#5A5A5A]" />
            </label>
            {requiresPhonePassword && (
              <label className="md:col-span-2">
                <span className="text-sm font-medium text-[#1A1A1A]">Current password required to change login phone</span>
                <input
                  type="password"
                  value={phonePassword}
                  onChange={(event) => setPhonePassword(event.target.value)}
                  className="mt-2 w-full rounded-2xl border border-[#D8D5D0] bg-[#F7F4EE] px-4 py-3 text-sm outline-none focus:border-[#9F1D35]"
                  placeholder="Current password"
                />
              </label>
            )}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={saveProfileAndPreferences}
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60 sm:w-fit"
            >
              <Save className="h-4 w-4" />
              Save profile
            </button>
          </div>
        </div>
      </div>
    );
  })();

  if (loading) {
    return <div className="min-h-screen bg-[#F7F4EE] p-8 text-[#5A5A5A]">Loading settings...</div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] px-4 py-7 sm:px-6" data-testid="settings-page">
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="caption text-[#9F1D35]">Account settings</p>
            <h1 className="mt-2 font-serif text-4xl font-semibold text-[#1A1A1A]">Settings and preferences</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#5A5A5A]">
              Manage safe account details, notification channels, and role preferences.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#D8D5D0] bg-[#FAFAF7] px-4 py-2 text-sm text-[#5A5A5A]">
            <Mail className="h-4 w-4 text-[#9F1D35]" />
            {accountUser.email || accountUser.phone || 'Magnolia account'}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          <nav className="h-fit overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-2">
            <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const active = activeSection === section.key;
                return (
                  <button
                    key={section.key}
                    type="button"
                    onClick={() => setActiveSection(section.key)}
                    className={`inline-flex items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                      active ? 'bg-[#9F1D35] text-[#FAFAF7]' : 'text-[#5A5A5A] hover:bg-[#F5E8EA] hover:text-[#1A1A1A]'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </nav>

          <section>{renderSection}</section>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
